"use client";

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Lang = "th" | "en";

type Entry = { en: string; th: string };

/**
 * UI string dictionary (English + Thai). Grows as components are migrated.
 * Keys are dot-namespaced by surface: nav.*, soap.*, order.*, common.*, etc.
 * Clinical NOTE CONTENT is not translated here — that is produced bilingually
 * by CARIVA. This dictionary is the app chrome only.
 */
export const strings: Record<string, Entry> = {
  // App shell / nav
  "app.name": { en: "Sati Medical Portal", th: "สติ เมดิคอล พอร์ทัล" },
  "nav.back": { en: "Back to patient list", th: "กลับไปหน้ารายชื่อผู้ป่วย" },
  "nav.dashboard": { en: "Dashboard", th: "แดชบอร์ด" },
  "nav.patients": { en: "Patients", th: "ผู้ป่วย" },
  "nav.rounds": { en: "Rounds", th: "เยี่ยมตรวจผู้ป่วย" },
  "nav.search.placeholder": { en: "Find patient or record…", th: "ค้นหาผู้ป่วยหรือเวชระเบียน…" },
  "nav.search.aria": { en: "Global search", th: "ค้นหาทั้งระบบ" },
  "nav.notifications": { en: "Notifications", th: "การแจ้งเตือน" },
  "search.empty": { en: "No patient or record matches", th: "ไม่พบผู้ป่วยหรือเวชระเบียนที่ตรงกับ" },
  "user.role.cardiologist": { en: "Senior Cardiologist", th: "อายุรแพทย์หัวใจอาวุโส" },
  "lang.toggle.aria": { en: "Interface language", th: "ภาษาของระบบ" },

  // SOAP
  "soap.title": { en: "Structured SOAP Note", th: "บันทึก SOAP แบบมีโครงสร้าง" },
  "soap.subjective": { en: "Subjective", th: "อาการ (Subjective)" },
  "soap.subjective.hint": { en: "Symptoms & patient-reported evidence", th: "อาการและข้อมูลที่ผู้ป่วยบอก" },
  "soap.objective": { en: "Objective", th: "ตรวจร่างกาย (Objective)" },
  "soap.objective.hint": { en: "Exam, vitals, labs & imaging", th: "การตรวจ สัญญาณชีพ แล็บ และภาพถ่าย" },
  "soap.assessment": { en: "Assessment", th: "การประเมิน (Assessment)" },
  "soap.assessment.hint": { en: "Recorded diagnoses & clinical status", th: "การวินิจฉัยและสถานะทางคลินิก" },
  "soap.plan": { en: "Plan", th: "แผนการรักษา (Plan)" },
  "soap.plan.hint": { en: "Treatment, monitoring & orders", th: "การรักษา การติดตาม และคำสั่ง" },
  "soap.scribe": { en: "Scribe all SOAP", th: "ถอดเสียงทั้ง SOAP" },
  "soap.dictate": { en: "Dictate", th: "พูดบันทึก" },
  "soap.convertPlan": { en: "Convert Plan to Doctor Orders", th: "แปลงแผนเป็นคำสั่งแพทย์" },
  "soap.convertPlan.hint": { en: "Parses each line of the Plan into draft orders", th: "แปลงแต่ละบรรทัดของแผนเป็นร่างคำสั่ง" },
  "soap.convert.btn": { en: "Convert to order", th: "แปลงเป็นคำสั่ง" },
  "soap.structuring": { en: "Structuring", th: "กำลังจัดโครงสร้าง" },
  "soap.clear": { en: "Clear SOAP", th: "ล้าง SOAP" },
  "soap.finalize": { en: "Finalize Clinical Note", th: "ยืนยันบันทึกเวชกรรม" },
  "soap.finalized": { en: "Clinical Note Finalized", th: "ยืนยันบันทึกแล้ว" },
  "soap.footer.evidence": { en: "Evidence-grounded · human-approved", th: "อ้างอิงหลักฐาน · แพทย์อนุมัติ" },
  "soap.placeholder": { en: "Document this section…", th: "บันทึกข้อมูลส่วนนี้…" },
  "soap.evidence": { en: "Evidence", th: "หลักฐาน" },
  "soap.generating": { en: "Generating…", th: "กำลังสร้าง…" },
  "soap.ai.autopilot": { en: "AI · auto-pilot", th: "AI · อัตโนมัติ" },
  "soap.accept.tab": { en: "Accept with Tab", th: "กด Tab เพื่อยอมรับ" },
  "soap.dismiss.esc": { en: "Dismiss with Esc", th: "กด Esc เพื่อปิด" },

  // Orders
  "order.oneDay": { en: "One-Day / STAT", th: "วันเดียว / ด่วน" },
  "order.continuous": { en: "Continuous / Standing", th: "ต่อเนื่อง / ประจำ" },
  "order.dragHint": { en: "Drag a card here to move it.", th: "ลากการ์ดมาที่นี่เพื่อย้าย" },
  "order.usage": { en: "Usage", th: "วิธีใช้" },
  "order.stat": { en: "STAT", th: "ด่วน" },
  "order.oneDay.empty.title": { en: "No one-day orders yet", th: "ยังไม่มีคำสั่งแบบวันเดียว" },
  "order.oneDay.empty.hint": { en: "Parsed orders and Heartbeat conversions land here.", th: "คำสั่งที่แปลงจากแผนและ Heartbeat จะมาอยู่ที่นี่" },
  "order.continuous.empty.title": { en: "No standing orders yet", th: "ยังไม่มีคำสั่งแบบประจำ" },
  "order.continuous.empty.hint": { en: "Ongoing medications and fluids appear here.", th: "ยาและสารน้ำแบบต่อเนื่องจะปรากฏที่นี่" },

  // Order entry (omnibox)
  "order.entry.placeIn": { en: "Place new order in", th: "ใส่คำสั่งใหม่ในเลน" },
  "order.entry.auto": { en: "Auto-detect", th: "ตรวจอัตโนมัติ" },
  "order.entry.routingHint": { en: "Select Continuous for ongoing drugs or fluids, or include \u201Ccontinue\u201D, an infusion rate, or a recurring schedule when using Auto-detect.", th: "เลือก \u201Cต่อเนื่อง\u201D สำหรับยา/สารน้ำที่ให้ต่อเนื่อง หรือระบุคำว่า \u201Cต่อเนื่อง\u201D อัตราหยด หรือความถี่ เมื่อใช้ตรวจอัตโนมัติ" },
  "order.entry.recording": { en: "Recording orders", th: "กำลังบันทึกคำสั่ง" },
  "order.entry.recording.hint": { en: "No order text is entered while recording.", th: "จะไม่มีการกรอกคำสั่งขณะบันทึกเสียง" },
  "order.entry.stopSummarize": { en: "Stop & summarize", th: "หยุดและสรุป" },
  "order.entry.summarizing": { en: "Nong SATI is summarizing the order", th: "Nong SATI กำลังสรุปคำสั่ง" },
  "order.entry.summarizing.hint": { en: "Nothing will be entered until you confirm the result.", th: "จะยังไม่กรอกจนกว่าคุณจะยืนยันผล" },
  "order.entry.confirmTitle": { en: "Confirm dictated order summary", th: "ยืนยันสรุปคำสั่งจากเสียง" },
  "order.entry.confirmHint": { en: "Review or edit before creating any order chips.", th: "ตรวจทานหรือแก้ไขก่อนสร้างการ์ดคำสั่ง" },
  "order.entry.live": { en: "Nong SATI Live", th: "Nong SATI สด" },
  "order.entry.sim": { en: "Safe simulation", th: "โหมดจำลอง" },
  "order.entry.heard": { en: "What was heard", th: "สิ่งที่ได้ยิน" },
  "order.entry.editable": { en: "AI summarization — editable", th: "สรุปโดย AI — แก้ไขได้" },
  "order.entry.reviewNote": { en: "The confirmed summary will be structured into draft order chips; the doctor remains the final author.", th: "สรุปที่ยืนยันจะถูกจัดเป็นร่างคำสั่ง โดยแพทย์ยังเป็นผู้ตัดสินใจสุดท้าย" },
  "order.entry.reRecord": { en: "Re-record", th: "อัดใหม่" },
  "order.entry.confirmCreate": { en: "Confirm & create order drafts", th: "ยืนยันและสร้างร่างคำสั่ง" },
  "order.entry.search.aria": { en: "Intelligent order search", th: "ค้นหาคำสั่งอัจฉริยะ" },
  "order.entry.search.placeholder": { en: "Search a drug/lab/procedure, or type free text e.g. NSS 1000 mL 80 cc/hr continue", th: "ค้นหายา/แล็บ/หัตถการ หรือพิมพ์อิสระ เช่น NSS 1000 mL 80 cc/hr ต่อเนื่อง" },
  "order.entry.add": { en: "Add order", th: "เพิ่มคำสั่ง" },
  "order.entry.structuring": { en: "Structuring…", th: "กำลังจัดโครงสร้าง…" },
  "order.entry.dictate": { en: "Dictate orders", th: "พูดสั่งคำสั่ง" },
  "order.entry.demo": { en: "Use demo order", th: "ใช้ตัวอย่างคำสั่ง" },
  "order.entry.browse": { en: "Browse medicines", th: "เลือกดูรายการยา" },
  "order.entry.tmtHead": { en: "TMT drug formulary · click to add", th: "รายการยา TMT · คลิกเพื่อเพิ่ม" },
  "order.entry.catalogHead": { en: "Catalog matches · click to add", th: "รายการที่ตรงกัน · คลิกเพื่อเพิ่ม" },
  "order.entry.quickAdd": { en: "Quick add to selected lane", th: "เพิ่มด่วนในเลนที่เลือก" },
  "order.entry.addBundle": { en: "Add all", th: "เพิ่มทั้งหมด" },
  "order.entry.laneSearch": { en: "Add to this lane — search drug / lab / procedure", th: "เพิ่มในเลนนี้ — ค้นหายา / แล็บ / หัตถการ" },
  "order.cat.medication": { en: "Medication", th: "ยา" },
  "order.cat.lab": { en: "Lab", th: "แล็บ" },
  "order.cat.procedure": { en: "Procedure", th: "หัตถการ" },
  "order.cat.other": { en: "Other", th: "อื่นๆ" },

  // Patient status (display only — className still uses the raw value)
  "status.critical": { en: "Critical", th: "วิกฤต" },
  "status.watch": { en: "Watch", th: "เฝ้าระวัง" },
  "status.stable": { en: "Stable", th: "คงที่" },

  // Ward list
  "ward.service": { en: "MEDICINE SERVICE", th: "แผนกอายุรกรรม" },
  "ward.todayRound": { en: "Today's ward round", th: "ราวด์ผู้ป่วยวันนี้" },
  "ward.todayRound.sub": { en: "Six inpatients are ready for review. Open a chart to write, order, and finalize in one clinical workspace.", th: "มีผู้ป่วยใน 6 รายพร้อมให้ตรวจ เปิดเวชระเบียนเพื่อเขียน สั่ง และยืนยันในที่เดียว" },
  "ward.roundCompleteness": { en: "ROUND COMPLETENESS", th: "ความคืบหน้าของราวด์" },
  "ward.notesInProgress": { en: "notes in progress", th: "บันทึกกำลังดำเนินการ" },
  "ward.list.title": { en: "Medicine 2 · Ward list", th: "อายุรกรรม 2 · รายชื่อผู้ป่วย" },
  "ward.list.sub": { en: "Friday, 24 July 2026 · Morning round", th: "ศุกร์ 24 กรกฎาคม 2026 · ราวด์เช้า" },
  "ward.sso": { en: "SSO protected", th: "ป้องกันด้วย SSO" },
  "ward.addPatient": { en: "Add patient", th: "เพิ่มผู้ป่วย" },
  "ward.col.patient": { en: "Patient", th: "ผู้ป่วย" },
  "ward.col.location": { en: "Location", th: "ตำแหน่ง" },
  "ward.col.problem": { en: "Primary problem", th: "ปัญหาหลัก" },
  "ward.col.status": { en: "Status", th: "สถานะ" },
  "ward.col.activity": { en: "Last activity", th: "กิจกรรมล่าสุด" },
  "ward.bed": { en: "Bed", th: "เตียง" },
  "ward.openChart": { en: "Open chart", th: "เปิดเวชระเบียน" },
  "ward.view": { en: "View", th: "ดู" },
  "ward.viewChart": { en: "View chart", th: "ดูเวชระเบียน" },
  "ward.demoHint": { en: "Demo patient: open Mrs. Athena Test to enter the Progress Note AI workspace.", th: "ผู้ป่วยตัวอย่าง: เปิด นางอาเทน่า ทดสอบ เพื่อเข้าสู่พื้นที่ทำงาน Progress Note AI" },

  // Dashboard
  "dash.overview": { en: "SERVICE OVERVIEW", th: "ภาพรวมบริการ" },
  "dash.sub": { en: "Live snapshot of the Medicine 2 service — census, documentation readiness, and AI activity.", th: "ภาพรวมสดของแผนกอายุรกรรม 2 — จำนวนผู้ป่วย ความพร้อมของบันทึก และกิจกรรม AI" },
  "dash.acuityMix": { en: "ACUITY MIX", th: "สัดส่วนความรุนแรง" },
  "dash.critical.suffix": { en: "critical", th: "วิกฤต" },
  "dash.watch.suffix": { en: "watch", th: "เฝ้าระวัง" },
  "dash.stable.suffix": { en: "stable", th: "คงที่" },
  "dash.metric.admitted": { en: "Admitted patients", th: "ผู้ป่วยที่รับไว้" },
  "dash.metric.admitted.hint": { en: "Medicine 2 census", th: "จำนวนผู้ป่วยอายุรกรรม 2" },
  "dash.metric.notes": { en: "Notes in progress", th: "บันทึกกำลังดำเนินการ" },
  "dash.metric.notes.hint": { en: "68% round completeness", th: "ราวด์เสร็จ 68%" },
  "dash.metric.gaps": { en: "Claim-critical gaps", th: "จุดเสี่ยงต่อการเบิกจ่าย" },
  "dash.metric.gaps.hint": { en: "Across active problems", th: "จากปัญหาที่กำลังรักษา" },
  "dash.metric.alerts": { en: "Heartbeat alerts", th: "การแจ้งเตือน Heartbeat" },
  "dash.metric.alerts.hint": { en: "Awaiting clinician review", th: "รอแพทย์ตรวจสอบ" },
  "dash.priority": { en: "Priority patients", th: "ผู้ป่วยสำคัญเร่งด่วน" },
  "dash.priority.sub": { en: "Highest acuity first", th: "เรียงตามความรุนแรงมากไปน้อย" },
  "dash.startRounds": { en: "Start rounds", th: "เริ่มราวด์" },
  "dash.aiActivity": { en: "AI activity", th: "กิจกรรม AI" },
  "dash.aiActivity.sub": { en: "Today · Nong SATI", th: "วันนี้ · Nong SATI" },
  "dash.activity.suggestions": { en: "Heartbeat suggestions surfaced · 7 accepted", th: "คำแนะนำ Heartbeat · ยอมรับ 7 รายการ" },
  "dash.activity.scribed": { en: "SOAP notes scribed from dictation", th: "บันทึก SOAP จากการพูด" },
  "dash.activity.orders": { en: "orders parsed and confirmed", th: "คำสั่งที่แปลงและยืนยันแล้ว" },
  "dash.activity.gaps": { en: "claim-critical gaps flagged before discharge", th: "จุดเสี่ยงการเบิกจ่ายที่พบก่อนจำหน่าย" },

  // Rounds
  "rounds.morning": { en: "MORNING ROUND", th: "ราวด์เช้า" },
  "rounds.sub": { en: "Work through the ward in acuity order. Each card opens the full Progress Note AI workspace.", th: "ตรวจผู้ป่วยเรียงตามความรุนแรง แต่ละการ์ดเปิดพื้นที่ทำงาน Progress Note AI เต็มรูปแบบ" },
  "rounds.progress": { en: "ROUND PROGRESS", th: "ความคืบหน้าราวด์" },
  "rounds.reviewed": { en: "Charts reviewed today", th: "เวชระเบียนที่ตรวจแล้ววันนี้" },

  // Heartbeat feed
  "hb.title": { en: "Heartbeat Feed", th: "ฟีด Heartbeat" },
  "hb.autocorrect": { en: "Auto-correct all", th: "แก้ให้อัตโนมัติทั้งหมด" },
  "hb.autocorrect.title": { en: "Send every alert to SOAP and draft each order for review", th: "ส่งทุกการแจ้งเตือนไปยัง SOAP และร่างคำสั่งเพื่อตรวจทาน" },
  "hb.sendTo": { en: "Send to", th: "ส่งไปที่" },
  "hb.sentToSoap": { en: "Sent to SOAP", th: "ส่งไป SOAP แล้ว" },
  "hb.draftOrder": { en: "Draft order", th: "ร่างคำสั่ง" },
  "hb.orderDrafted": { en: "Order drafted", th: "ร่างคำสั่งแล้ว" },
  "hb.handled": { en: "Handled", th: "จัดการแล้ว" },
  "hb.snooze": { en: "Snooze", th: "เลื่อนไว้ก่อน" },
  "hb.footer": { en: "Every action creates a reviewable draft", th: "ทุกการกระทำจะสร้างร่างที่ตรวจทานได้" },
  "status.handled": { en: "handled", th: "จัดการแล้ว" },
  "status.snoozed": { en: "snoozed", th: "เลื่อนไว้" },

  // Heartbeat demo alert cards (seed sample data)
  "hb.item.interaction.title": { en: "Drug-Drug Interaction", th: "ยาตีกัน (Drug-Drug Interaction)" },
  "hb.item.interaction.body": { en: "Warfarin + Amoxicillin may increase INR", th: "Warfarin + Amoxicillin อาจเพิ่มค่า INR" },
  "hb.item.interaction.details": { en: "Consider closer INR monitoring if both medications are continued. Nong SATI is not changing the medication plan.", th: "พิจารณาติดตามค่า INR ใกล้ชิดขึ้นหากให้ยาทั้งสองต่อ Nong SATI ไม่ได้เปลี่ยนแผนการใช้ยา" },
  "hb.item.interaction.evidence": { en: "MAR review · interaction rules v4", th: "ทบทวน MAR · กฎการตีกันของยา v4" },
  "hb.item.vitals.title": { en: "Vitals Stabilizing", th: "สัญญาณชีพเริ่มคงที่" },
  "hb.item.vitals.body": { en: "SBP trending within 110–130 range", th: "ความดันตัวบนอยู่ในช่วง 110–130" },
  "hb.item.vitals.details": { en: "Eight recent readings are stable with no new threshold breach.", th: "ค่าที่วัดล่าสุด 8 ครั้งคงที่ ไม่มีค่าผิดเกณฑ์ใหม่" },
  "hb.item.vitals.evidence": { en: "Bedside monitor · last 8 observations", th: "มอนิเตอร์ข้างเตียง · 8 ค่าล่าสุด" },
  "hb.item.troponin.title": { en: "Lab Reminder", th: "เตือนรายการแล็บ" },
  "hb.item.troponin.body": { en: "Troponin draw in 2 hours", th: "เจาะ Troponin ในอีก 2 ชั่วโมง" },
  "hb.item.troponin.details": { en: "Scheduled order is due at 14:00. Responsibility remains with the care team.", th: "คำสั่งที่นัดไว้ครบกำหนด 14:00 ความรับผิดชอบยังอยู่กับทีมผู้รักษา" },
  "hb.item.troponin.evidence": { en: "Order schedule · due 14:00", th: "ตารางคำสั่ง · ครบกำหนด 14:00" },
  "hb.item.new-gap.title": { en: "Documentation Gap", th: "บันทึกไม่ครบ (Documentation Gap)" },
  "hb.item.new-gap.body": { en: "Bleeding status is not yet explicit", th: "ยังไม่ได้ระบุสถานะเลือดออกชัดเจน" },
  "hb.item.new-gap.details": { en: "Claim-critical for Anemia. A grounded suggestion is available for clinician review.", th: "สำคัญต่อการเบิกจ่ายสำหรับภาวะโลหิตจาง มีคำแนะนำอ้างอิงหลักฐานให้แพทย์ตรวจสอบ" },
  "hb.item.new-gap.evidence": { en: "CBC 08:00 · Anemia evidence schema", th: "CBC 08:00 · โครงหลักฐานภาวะโลหิตจาง" },

  // Patient banner
  "pb.allergy": { en: "Allergy", th: "แพ้ยา" },
  "pb.live": { en: "LIVE", th: "สด" },
  "pb.takeOrder.pre": { en: "Ordering on behalf of", th: "สั่งการแทน" },
  "pb.takeOrder.post": { en: "both identities recorded", th: "บันทึกทั้งสองตัวตน" },

  // Template modal
  "tpl.preview": { en: "Template preview · pending only", th: "ตัวอย่างเทมเพลต · รอยืนยันเท่านั้น" },
  "tpl.reviewing": { en: "Reviewing template…", th: "กำลังตรวจสอบเทมเพลต…" },
  "tpl.subtitle.fallback": { en: "Checking the order set against this patient.", th: "กำลังตรวจชุดคำสั่งกับผู้ป่วยรายนี้" },
  "tpl.loading": { en: "Cross-checking allergies and duplicate therapy…", th: "กำลังตรวจการแพ้ยาและยาซ้ำซ้อน…" },
  "tpl.soapSkeleton": { en: "SOAP skeleton", th: "โครงร่าง SOAP" },
  "tpl.pendingOrders": { en: "Pending orders", th: "คำสั่งที่รอยืนยัน" },
  "tpl.excluded": { en: "excluded from commit", th: "ไม่รวมเมื่อยืนยัน" },
  "tpl.conflictTitle": { en: "1 conflict requires review", th: "มี 1 รายการขัดแย้งที่ต้องตรวจสอบ" },
  "tpl.conflictBody": { en: "Amoxicillin will stay pending because a Penicillin allergy is recorded.", th: "Amoxicillin จะยังไม่ถูกยืนยันเพราะมีประวัติแพ้ Penicillin" },
  "tpl.commit": { en: "Pre-populate SOAP & Orders", th: "เติม SOAP และคำสั่งล่วงหน้า" },

  // Audit drawer
  "audit.kicker": { en: "Immutable provenance", th: "ที่มาที่แก้ไขไม่ได้" },
  "audit.title": { en: "Access Audit Log", th: "บันทึกการเข้าถึงและแก้ไข" },
  "audit.sub": { en: "Every edit, attribution change, and AI acceptance is versioned.", th: "ทุกการแก้ไข การเปลี่ยนผู้รับผิดชอบ และการยอมรับ AI ถูกบันทึกเป็นเวอร์ชัน" },
  "audit.takeOrder": { en: "TAKE ORDER", th: "สั่งการแทน" },
  "audit.doctorInput": { en: "Doctor input / source", th: "ข้อมูลจากแพทย์ / ที่มา" },
  "audit.aiSuggestion": { en: "AI suggestion", th: "คำแนะนำ AI" },
  "audit.selected": { en: "Selected by doctor", th: "แพทย์เลือก" },
  "audit.agent": { en: "Agent", th: "เอเจนต์" },
  "prov.AI-suggested → accepted": { en: "AI-suggested → accepted", th: "AI แนะนำ → ยอมรับ" },
  "prov.AI-dictated → confirmed": { en: "AI-dictated → confirmed", th: "AI ถอดเสียง → ยืนยัน" },
  "prov.AI-dictated → staged": { en: "AI-dictated → staged", th: "AI ถอดเสียง → รอยืนยัน" },
  "prov.Heartbeat → staged": { en: "Heartbeat → staged", th: "Heartbeat → รอยืนยัน" },
  "prov.User-entered": { en: "User-entered", th: "ผู้ใช้กรอกเอง" },
  "prov.Template → confirmed": { en: "Template → confirmed", th: "เทมเพลต → ยืนยัน" },

  // Full-SOAP dictation label
  "soap.allFull": { en: "full SOAP", th: "SOAP ทั้งหมด" },

  // Toast messages
  "toast.autopilotOn": { en: "Auto-pilot ON — AI auto-fills, flagged in blue for review", th: "เปิดออโต้ไพลอต — AI เติมให้อัตโนมัติ ทำเครื่องหมายสีน้ำเงินเพื่อตรวจทาน" },
  "toast.autopilotOff": { en: "Auto-pilot off — suggestions require review", th: "ปิดออโต้ไพลอต — คำแนะนำต้องผ่านการตรวจทาน" },
  "toast.demoChart": { en: "full chart is available for the demo patient (Mrs. Athena Test).", th: "เปิดเวชระเบียนเต็มได้เฉพาะผู้ป่วยตัวอย่าง (นางอาเทน่า ทดสอบ)" },
  "toast.addedTo": { en: "Added to", th: "เพิ่มไปที่" },
  "toast.evidenceAttached": { en: "evidence attached", th: "แนบหลักฐานแล้ว" },
  "toast.acceptedAllPre": { en: "Accepted all", th: "ยอมรับทั้งหมด" },
  "toast.acceptedAllPost": { en: "suggestions into the note", th: "คำแนะนำเข้าสู่บันทึก" },
  "toast.ordersAdded": { en: "order(s) added for review", th: "คำสั่งถูกเพิ่มเพื่อตรวจทาน" },
  "toast.noOrders": { en: "No orders to approve yet", th: "ยังไม่มีคำสั่งให้อนุมัติ" },
  "toast.ordersApprovedSaved": { en: "order(s) approved & saved to Aurora", th: "คำสั่งอนุมัติและบันทึกไป Aurora แล้ว" },
  "toast.ordersApproved": { en: "order(s) approved & saved", th: "คำสั่งอนุมัติและบันทึกแล้ว" },
  "toast.soapCleared": { en: "SOAP note and pending suggestions cleared", th: "ล้างบันทึก SOAP และคำแนะนำที่ค้างแล้ว" },
  "toast.ordersCleared": { en: "All draft orders and order-entry fields cleared", th: "ล้างร่างคำสั่งและช่องกรอกคำสั่งทั้งหมดแล้ว" },
  "toast.planEmpty": { en: "Plan is empty — dictate or type a Plan first, then convert to orders", th: "แผนยังว่าง — พูดหรือพิมพ์แผนก่อน แล้วจึงแปลงเป็นคำสั่ง" },
  "toast.noOrderable": { en: "No orderable items found in the Plan", th: "ไม่พบรายการที่สั่งได้ในแผน" },
  "toast.ordersFromPlan": { en: "order(s) created from the Plan", th: "คำสั่งที่สร้างจากแผน" },
  "toast.takeOrderOn": { en: "Take Order attribution is active", th: "เปิดการสั่งการแทนแล้ว" },
  "toast.takeOrderOff": { en: "Direct attribution restored", th: "กลับสู่การระบุตัวตนโดยตรง" },
  "toast.scribeRecording": { en: "Scribe recording — one pass fills all four SOAP fields + Doctor Orders (CARIVA ASR when enabled)", th: "กำลังบันทึกเสียง — บันทึกครั้งเดียวเติมครบทั้ง 4 ช่อง SOAP + คำสั่งแพทย์ (ใช้ CARIVA ASR เมื่อเปิดใช้)" },
  "toast.micUnavailable": { en: "Mic unavailable — Scribe will use the simulation transcript", th: "ไมโครโฟนไม่พร้อม — จะใช้ข้อความจำลองแทน" },
  "toast.recordingForPre": { en: "Recording for", th: "กำลังบันทึกสำหรับ" },
  "toast.recordingForPost": { en: "note text will not change until you review it", th: "ข้อความบันทึกจะไม่เปลี่ยนจนกว่าจะตรวจทาน" },
  "toast.scribeFilled": { en: "Scribe filled SOAP (review the AI text)", th: "Scribe เติม SOAP แล้ว (ตรวจทานข้อความ AI)" },
  "toast.orders": { en: "order(s)", th: "คำสั่ง" },
  "toast.dictationReady": { en: "Dictation ready in", th: "พร้อมบันทึกใน" },
  "toast.templateAddedConflictPre": { en: "Template added —", th: "เพิ่มเทมเพลตแล้ว —" },
  "toast.templateAddedConflictPost": { en: "conflict kept out of the order list", th: "รายการขัดแย้งถูกกันออกจากคำสั่ง" },
  "toast.templateAdded": { en: "Template added to the note and orders", th: "เพิ่มเทมเพลตเข้าบันทึกและคำสั่งแล้ว" },
  "toast.templateSavedPre": { en: "Template", th: "เทมเพลต" },
  "toast.templateSavedPost": { en: "saved — available in the Templates menu", th: "บันทึกแล้ว — อยู่ในเมนูเทมเพลต" },
  "toast.noteFinalizedPersisted": { en: "Clinical note finalized & persisted", th: "ยืนยันและบันทึกบันทึกเวชกรรมแล้ว" },
  "toast.noteFinalized": { en: "Clinical note finalized — new audit version created", th: "ยืนยันบันทึกเวชกรรมแล้ว — สร้างเวอร์ชันตรวจสอบใหม่" },
  "toast.savedAuroraPre": { en: "Saved to Aurora —", th: "บันทึกไป Aurora แล้ว —" },
  "toast.savedPre": { en: "Saved —", th: "บันทึกแล้ว —" },
  "toast.soapFields": { en: "SOAP field(s)", th: "ช่อง SOAP" },
  "toast.returnedWard": { en: "Returned to ward list.", th: "กลับสู่รายชื่อผู้ป่วยแล้ว" },

  // Help desk panel
  "help.title": { en: "Sati Help Desk", th: "ศูนย์ช่วยเหลือ Sati" },
  "help.agentLive": { en: "Agent live", th: "เอเจนต์ออนไลน์" },
  "help.sim": { en: "Simulation", th: "โหมดจำลอง" },
  "help.thinking": { en: "Reviewing the chart…", th: "กำลังตรวจเวชระเบียน…" },
  "help.placeholder": { en: "Ask AI anything…", th: "ถาม AI ได้ทุกเรื่อง…" },
  "help.q1": { en: "What's the transfusion threshold for this Hb?", th: "เกณฑ์การให้เลือดสำหรับค่า Hb นี้คือเท่าไร?" },
  "help.q2": { en: "Any interaction with Warfarin?", th: "มียาตีกันกับ Warfarin ไหม?" },
  "help.q3": { en: "Which documentation gaps remain?", th: "ยังมีจุดบันทึกที่ขาดตรงไหนบ้าง?" },
  "help.q4": { en: "Should I order a cardiology consult?", th: "ควรปรึกษาแพทย์หัวใจไหม?" },
  "help.seed1": { en: "Hi Dr. Heartbeat. I've analyzed the recent ECG. There is a slight prolongation in PR interval compared to baseline.", th: "สวัสดีค่ะ คุณหมอ ฉันวิเคราะห์ ECG ล่าสุดแล้ว พบว่า PR interval ยาวขึ้นเล็กน้อยเทียบกับค่าเดิม" },
  "help.seed2": { en: "I'd review reversible factors first. Magnesium was borderline yesterday at 1.6 mg/dL; please use your clinical judgment.", th: "แนะนำให้ตรวจปัจจัยที่แก้ไขได้ก่อน เมื่อวานค่าแมกนีเซียมค่อนข้างต่ำที่ 1.6 mg/dL โปรดใช้ดุลยพินิจทางคลินิก" },

  // Clinical viewer — chrome
  "cv.title": { en: "Clinical Viewer", th: "มุมมองข้อมูลคลินิก" },
  "cv.hide": { en: "Hide", th: "ซ่อน" },
  "cv.show": { en: "Show Clinical Viewer", th: "แสดงมุมมองข้อมูลคลินิก" },
  "cv.search": { en: "Find results, notes, or files…", th: "ค้นหาผล บันทึก หรือไฟล์…" },
  "cv.synced": { en: "Synced 2 min ago", th: "ซิงค์เมื่อ 2 นาทีที่แล้ว" },
  "cv.pinnedHead": { en: "Pinned while you review other tabs", th: "ปักหมุดไว้ขณะดูแท็บอื่น" },
  "cv.unpin": { en: "Unpin", th: "เลิกปักหมุด" },
  "cv.pin": { en: "Pin", th: "ปักหมุด" },
  "cv.pinned": { en: "Pinned", th: "ปักหมุดแล้ว" },
  "cv.empty": { en: "No clinical data matches", th: "ไม่พบข้อมูลคลินิกที่ตรงกับ" },
  "cv.clearSearch": { en: "Clear search", th: "ล้างการค้นหา" },
  "cv.section.labs": { en: "Labs & results", th: "ผลแล็บและผลตรวจ" },
  "cv.section.vitals": { en: "Vital trends", th: "แนวโน้มสัญญาณชีพ" },
  "cv.section.conditions": { en: "Underlying conditions", th: "โรคประจำตัว" },
  "cv.section.pacs": { en: "PACS & scanned documents", th: "ภาพ PACS และเอกสารสแกน" },
  "cv.section.notes": { en: "Prior progress notes", th: "บันทึกความก้าวหน้าก่อนหน้า" },
  "cv.section.mar": { en: "Medication administration", th: "การให้ยา" },
  "cv.alert.labs": { en: "3 abnormal", th: "ผิดปกติ 3 ค่า" },
  "cv.alert.pacs": { en: "3 files", th: "3 ไฟล์" },

  "order.cat.fluid": { en: "Fluid", th: "สารน้ำ" },

  // Template builder modal
  "tb.title": { en: "New template — edit & save", th: "เทมเพลตใหม่ — แก้ไขและบันทึก" },
  "tb.hint": { en: "Prefilled from the current encounter. Edit the SOAP skeleton and order set, then save it as a reusable template.", th: "เติมจากการพบครั้งนี้ แก้ไขโครง SOAP และชุดคำสั่ง แล้วบันทึกเป็นเทมเพลตที่ใช้ซ้ำได้" },
  "tb.name": { en: "Template name", th: "ชื่อเทมเพลต" },
  "tb.name.ph": { en: "e.g. Anemia rounding set", th: "เช่น ชุดราวด์ผู้ป่วยโลหิตจาง" },
  "tb.desc": { en: "Description", th: "คำอธิบาย" },
  "tb.desc.ph": { en: "Short description", th: "คำอธิบายสั้นๆ" },
  "tb.subtitle.default": { en: "Custom template · saved from this encounter", th: "เทมเพลตกำหนดเอง · บันทึกจากการพบครั้งนี้" },
  "tb.orderSet": { en: "Order set", th: "ชุดคำสั่ง" },
  "tb.addOrder": { en: "Add order", th: "เพิ่มคำสั่ง" },
  "tb.empty": { en: "No orders yet. Add the routine orders for this template.", th: "ยังไม่มีคำสั่ง เพิ่มคำสั่งประจำสำหรับเทมเพลตนี้" },
  "tb.order.name.ph": { en: "Order name (e.g. Paracetamol 500 mg)", th: "ชื่อคำสั่ง (เช่น Paracetamol 500 mg)" },
  "tb.order.usage.ph": { en: "Usage (e.g. 1 tab PO q6h PRN)", th: "วิธีใช้ (เช่น 1 tab PO q6h PRN)" },
  "tb.templateTextSuffix": { en: "template text…", th: "ข้อความเทมเพลต…" },
  "tb.save": { en: "Save template", th: "บันทึกเทมเพลต" },

  // Clinical viewer — sub-panels
  "cv.labs.hbDrop": { en: "Hb decreased from 8.1 → 7.2 g/dL", th: "Hb ลดลงจาก 8.1 → 7.2 g/dL" },
  "cv.labs.hbDrop.sub": { en: "Compared with yesterday 08:00", th: "เทียบกับเมื่อวาน 08:00" },
  "cv.today": { en: "Today", th: "วันนี้" },
  "cv.chemistry": { en: "Chemistry", th: "เคมีในเลือด" },
  "cv.compareHistory": { en: "Compare full result history", th: "เทียบประวัติผลทั้งหมด" },
  "cv.vitals.bp": { en: "Blood pressure", th: "ความดันโลหิต" },
  "cv.vitals.pulse": { en: "Pulse", th: "ชีพจร" },
  "cv.vitals.temp": { en: "Temperature", th: "อุณหภูมิ" },
  "cv.vitals.stable": { en: "stable", th: "คงที่" },
  "cv.vitals.roomAir": { en: "room air", th: "อากาศห้อง" },
  "cv.vitals.latest": { en: "latest", th: "ล่าสุด" },
  "cv.vitals.micro": { en: "Eight observations · automatically synchronized from the bedside monitor.", th: "8 ค่าที่วัด · ซิงค์อัตโนมัติจากมอนิเตอร์ข้างเตียง" },
  "cv.active": { en: "Active", th: "กำลังรักษา" },
  "cv.conditions.history": { en: "Hypertension for 12 years. Type 2 diabetes treated with metformin; last HbA1c 7.4%.", th: "ความดันโลหิตสูงมา 12 ปี เบาหวานชนิดที่ 2 รักษาด้วย metformin; HbA1c ล่าสุด 7.4%" },
  "cv.notes.compare": { en: "Use as comparison", th: "ใช้เปรียบเทียบ" },
  "cv.pacs.connected": { en: "PACS connected", th: "เชื่อมต่อ PACS แล้ว" },
  "cv.pacs.summary": { en: "2 studies · 1 scanned document", th: "2 การตรวจ · 1 เอกสารสแกน" },
  "cv.preview.context": { en: "PATIENT CONTEXT · READ ONLY", th: "บริบทผู้ป่วย · อ่านอย่างเดียว" },
  "cv.preview.referral": { en: "Referral summary", th: "สรุปการส่งต่อ" },
  "cv.preview.referralBody": { en: "Previous anemia work-up reviewed. Iron profile and stool occult blood were requested at the referring hospital.", th: "ทบทวนการตรวจภาวะโลหิตจางก่อนหน้า มีการสั่งตรวจ iron profile และ stool occult blood ที่โรงพยาบาลที่ส่งต่อ" },
  "cv.preview.summary": { en: "Clinical summary", th: "สรุปทางคลินิก" },
  "cv.preview.source": { en: "Source", th: "แหล่งที่มา" },
  "cv.preview.reportedBy": { en: "Reported by", th: "รายงานโดย" },
  "cv.preview.patient": { en: "Patient", th: "ผู้ป่วย" },
  "cv.preview.openViewer": { en: "Open full viewer", th: "เปิดตัวแสดงผลเต็ม" },
  "cv.preview.export": { en: "Export copy", th: "ส่งออกสำเนา" },

  // Common actions
  "common.accept": { en: "Accept", th: "ยอมรับ" },
  "common.dismiss": { en: "Dismiss", th: "ปิด" },
  "common.cancel": { en: "Cancel", th: "ยกเลิก" },
  "common.confirm": { en: "Confirm", th: "ยืนยัน" },
  "common.delete": { en: "Delete", th: "ลบ" },
  "common.review": { en: "review", th: "ตรวจทาน" },
};

type Ctx = { lang: Lang; setLang: (l: Lang) => void; t: (key: string, fallback?: string) => string };

const LanguageContext = createContext<Ctx | null>(null);

function translate(key: string, lang: Lang, fallback?: string): string {
  const entry = strings[key];
  if (!entry) return fallback ?? key;
  return entry[lang] || entry.en || fallback || key;
}

function detectLang(): Lang {
  if (typeof window !== "undefined") {
    const saved = window.localStorage?.getItem("pnai.lang");
    if (saved === "th" || saved === "en") return saved;
    if (typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("th")) return "th";
  }
  return "en";
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  // Start "en" on both server and first client render (no hydration mismatch),
  // then resolve the real preference after mount.
  const [lang, setLangState] = useState<Lang>("en");
  useEffect(() => setLangState(detectLang()), []);
  const setLang = (l: Lang) => {
    setLangState(l);
    if (typeof window !== "undefined") window.localStorage?.setItem("pnai.lang", l);
  };
  const t = (key: string, fallback?: string) => translate(key, lang, fallback);
  return <LanguageContext.Provider value={{ lang, setLang, t }}>{children}</LanguageContext.Provider>;
}

/** Pick a display name by language: Thai in TH mode, English otherwise. */
export function localizedName(lang: Lang, name: string, englishName?: string): string {
  return lang === "th" ? name : englishName || name;
}

/** Access the current language + setter + translator. Safe without a provider. */
export function useLang(): Ctx {
  const ctx = useContext(LanguageContext);
  if (ctx) return ctx;
  return { lang: "en", setLang: () => {}, t: (k, f) => translate(k, "en", f) };
}
